import React, { FC } from 'react'

import { useFlag } from '@sketch/modules-common'
import { useGetTrash } from 'modules/shares/operations'

import LoadingState from '../LoadingState'
import TrashItemsLayout from '../TrashItemsLayout'
import TrashListEmptyState from '../TrashListEmptyState'

import {
  useForTablet,
  DocumentHead,
  ErrorMessage,
  DocumentHeader,
} from '@sketch/components'
import { WorkspaceMinimalFragment } from '@sketch/gql-types'
import { useSearch } from 'modules/shares/hooks/useSearch'

import { SKETCH_WEBSITE } from '@sketch/env-config'

interface TrashListProps {
  workspaceId: string
  userRole: WorkspaceMinimalFragment['userRole']
}

const TrashList: FC<TrashListProps> = ({ workspaceId, userRole }) => {
  const { searchDebounced: search } = useSearch()
  const isTabletAndBigger = useForTablet()

  /**
   * This FF should be removed from here on the ticket
   * https://github.com/sketch-hq/Cloud/issues/16669
   */
  const isOnboardingEmptyStatesOn = useFlag('onboarding-empty-states')
  const result = useGetTrash({ workspaceId, search })

  if (!result || result.kind === 'error') {
    return <ErrorMessage.Generic />
  }

  if (result.kind === 'loading') return <LoadingState />

  const { fetchMore, items, loading, totalCount } = result

  const header = isTabletAndBigger && (
    <DocumentHeader
      title="Trash"
      learnMoreTooltipTitle="Learn about deleting documents"
      learnMoreTooltipURL={`${SKETCH_WEBSITE}/docs/getting-started/saving-and-managing-documents/#deleting-documents`}
      description="Documents and projects in Trash are permanently deleted after 90 days."
    />
  )

  if (!loading && items.length === 0) {
    return (
      <>
        {isOnboardingEmptyStatesOn && header}
        <TrashListEmptyState search={search} />
      </>
    )
  }

  return (
    <>
      <DocumentHead title="Trash" />
      <TrashItemsLayout
        items={items}
        loading={loading}
        userRole={userRole}
        totalItemsCount={totalCount}
        onLoadMore={fetchMore}
        workspaceId={workspaceId}
        header={header}
      />
    </>
  )
}

export default TrashList
